import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { QBItemListResponse, QBAccountListResponse, QBCustomerListResponse } from '@schemas/api';

const useTenantQueryScope = () => useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

export const useSearchQBItems = (searchTerm: string, skip = 0, limit = 50, erpConnectionId?: string) => {
  const tenantScope = useTenantQueryScope();
  return useQuery({
    queryKey: ['qb-items', tenantScope, erpConnectionId ?? 'default', 'search', searchTerm, skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<QBItemListResponse>('/documents/qb-items', {
        params: { search: searchTerm || undefined, active_only: true, skip, limit, erp_connection_id: erpConnectionId },
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useSearchQBCustomers = (searchTerm: string, skip = 0, limit = 50, erpConnectionId?: string) => {
  const tenantScope = useTenantQueryScope();
  return useQuery({
    queryKey: ['qb-customers', tenantScope, erpConnectionId ?? 'default', 'search', searchTerm, skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<QBCustomerListResponse>('/qb-customers', {
        params: { search: searchTerm || undefined, skip, limit, erp_connection_id: erpConnectionId },
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useSearchQBAccounts = (searchTerm: string, accountType?: string, skip = 0, limit = 50, erpConnectionId?: string) => {
  const tenantScope = useTenantQueryScope();
  return useQuery({
    queryKey: ['qb-accounts', tenantScope, erpConnectionId ?? 'default', 'search', searchTerm, accountType, skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<QBAccountListResponse>('/documents/qb-accounts', {
        params: {
          search: searchTerm || undefined,
          account_type: accountType || undefined,
          active_only: true,
          skip,
          limit,
          erp_connection_id: erpConnectionId,
        },
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
};
